import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useInventory, useRefreshAll } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { localName } from "../../lib/format";
import type { InventoryItem } from "../../lib/types";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { DownloadButton } from "../ui/DownloadButton";
import { PrintButton } from "../ui/PrintButton";
import { Modal } from "../ui/Modal";

function IntakeDialog({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const refresh = useRefreshAll();
  const [qty, setQty] = useState(item.reorderQty ?? 50);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api.post(`/inventory/${item.productId}/intake`, { qty });
      toast(t("inventory.received", { qty, name: localName(item, locale) }), "success");
      refresh();
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal open onClose={onClose} title={t("inventory.intakeTitle", { name: localName(item, locale) })} size="sm"
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={busy || qty < 1} onClick={save}>{t("inventory.receive")}</button></>}>
      <div className="text-xs font-bold text-gray-600">{t("inventory.onHand")}: <span className="num">{item.qtyOnHand}</span> • {t("inventory.par")}: <span className="num">{item.parLevel}</span></div>
      <div>
        <label className="label" htmlFor="in-qty">{t("inventory.quantity")}</label>
        <input id="in-qty" className="field num font-mono" type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} autoFocus />
      </div>
    </Modal>
  );
}

const TONE = { ok: "green", low: "amber", critical: "red" } as const;
const BAR = { ok: "bg-emerald-500", low: "bg-amber-500", critical: "bg-red-500" } as const;

export function InventoryPanel() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { data, isLoading } = useInventory();
  const [intake, setIntake] = useState<InventoryItem | null>(null);
  const sorted = [...(data ?? [])].sort((a, b) => ({ critical: 0, low: 1, ok: 2 })[a.level] - ({ critical: 0, low: 1, ok: 2 })[b.level]);

  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("inventory.title")}>
      <SectionHeader icon="📦" title={t("inventory.title")} right={
        <div className="flex flex-wrap gap-2">
          <DownloadButton path={`/exports/inventory.xlsx?locale=${locale}`} fallbackName="inventory.xlsx">📊 Excel</DownloadButton>
          <DownloadButton path={`/exports/inventory.csv?locale=${locale}`} fallbackName="inventory.csv">CSV</DownloadButton>
          <DownloadButton path={`/documents/inventory-report?locale=${locale}`} fallbackName="inventory.pdf">📄 PDF</DownloadButton>
          <PrintButton path={`/documents/inventory-report?locale=${locale}`} />
        </div>
      } />
      {isLoading ? <Empty>{t("common.loading")}</Empty> : (
        <div className="space-y-2.5 text-xs">
          {sorted.map((i) => (
            <div key={i.productId} className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="font-black text-cosmic-navy">{localName(i, locale)}</div>
                <Pill tone={TONE[i.level]}>{t(`inventory.level.${i.level}`)}: <span className="num">{i.qtyOnHand}</span></Pill>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuenow={i.percentOfPar} aria-valuemin={0} aria-valuemax={100}>
                <div className={`h-full rounded-full ${BAR[i.level]}`} style={{ width: `${Math.max(3, i.percentOfPar)}%` }} />
              </div>
              <div className="mt-1 flex items-center justify-between text-[10px] font-medium text-gray-500">
                <span>{t("inventory.par")}: <span className="num">{i.parLevel}</span>{i.supplier ? ` • ${i.supplier}` : ""}</span>
                <button type="button" className="font-bold text-cosmic-purple hover:underline" onClick={() => setIntake(i)}>+ {t("inventory.receive")}</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {intake && <IntakeDialog item={intake} onClose={() => setIntake(null)} />}
    </section>
  );
}
